import {
    Box,
    CircularProgress,
    Button as MuiButton,
    ButtonProps as MuiButtonProps,
    styled,
    Tooltip,
} from '@mui/material';
import React, { useEffect } from 'react';
import { Link as RouterLink } from 'react-router';
import { useValueRef } from 'src/helpers/react';
import { useTrack } from 'src/lib/analytics';

import {
    AnalyticsEvent,
    AnalyticsEventType,
    ClickElementType,
    DisplayElementType,
} from '../interactionTypes';
import { Typography } from './Typography';

export type ButtonKind = 'filled' | 'outlined';

export type ButtonColor = 'primary' | 'critical';

export type ButtonSize = 'small' | 'standard' | 'large';

const StyledButton = styled(MuiButton, {
    shouldForwardProp: (prop) =>
        prop !== '$splitButtonArrow' && prop !== '$size' && prop !== '$iconOnly',
})<{ $splitButtonArrow: boolean; $size: ButtonSize; $iconOnly: boolean }>`
    padding: ${({ theme, $splitButtonArrow, $size }) => {
        const sizeFactor = $size === 'small' ? 0.75 : $size === 'large' ? 2.75 : 1;
        const rightLeft = $splitButtonArrow ? 1 : 2;
        return theme.spacing(1 * sizeFactor, rightLeft * sizeFactor);
    }};
    max-height: ${({ theme, $size }) => {
        return $size === 'small' ? theme.spacing(3.375) : theme.spacing(4.25);
    }};
    ${({ theme, $size, $iconOnly }) =>
        $iconOnly && {
            minWidth: theme.spacing($size === 'small' ? 3.5 : 4.25),
            width: theme.spacing($size === 'small' ? 3.5 : 4.25),
            height: theme.spacing($size === 'small' ? 3.5 : 4.25),
            maxHeight: 'none',
            flexShrink: 0,
            padding: theme.spacing(0.75),
            '& > svg': { width: 18, height: 18 },
        }};
    &.MuiButton-containedPrimary {
        color: ${({ theme }) => theme.resqPalette.text.onPrimary};
        background-color: ${({ theme }) => theme.resqPalette.surface.primary};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.primaryHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.primaryPressed};
        }
    }
    &.MuiButton-containedDanger {
        color: ${({ theme }) => theme.resqPalette.text.onCritical};
        background-color: ${({ theme }) => theme.resqPalette.surface.critical};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.criticalHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.criticalPressed};
        }
    }
    &.MuiButton-outlinedPrimary {
        color: ${({ theme }) => theme.resqPalette.text.primary};
        border-color: ${({ theme }) => theme.resqPalette.border.primary};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.primarySubduedHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.primarySubduedPressed};
            color: ${({ theme }) => theme.resqPalette.text.primaryPressed};
        }
    }
    &.MuiButton-outlinedDanger {
        color: ${({ theme }) => theme.resqPalette.text.critical};
        border-color: ${({ theme }) => theme.resqPalette.border.critical};
        :focus-visible,
        :hover {
            background-color: ${({ theme }) => theme.resqPalette.surface.criticalSubduedHovered};
        }
        :active {
            background-color: ${({ theme }) => theme.resqPalette.surface.criticalSubduedPressed};
            color: ${({ theme }) => theme.resqPalette.text.criticalPressed};
        }
    }
    white-space: nowrap;
    &.Mui-focusVisible {
        outline: 2px solid ${({ theme }) => theme.resqPalette.border.focused};
        outline-offset: 2px;
    }
    &.MuiButton-outlined {
        background-color: transparent;
    }
    &&.Mui-disabled {
        background-color: ${({ theme }) => theme.resqPalette.surface.disabled};
        color: ${({ theme }) => theme.resqPalette.text.disabled};
        border-color: ${({ theme }) => theme.resqPalette.border.subdued};
        opacity: 1;
    }
`;

export type BaseButtonProps = Pick<
    MuiButtonProps,
    | 'autoFocus'
    | 'children'
    | 'onClick'
    | 'onMouseDown'
    | 'type'
    | 'fullWidth'
    | 'startIcon'
    | 'endIcon'
    | 'disabled'
    | 'sx'
    | 'style'
    | 'className'
> & {
    size?: ButtonSize;
    trackingId?: string;
    trackingProperties?: Record<string, unknown>;
    /** Use when a parent surface owns the button's complete analytics contract. */
    disableAnalytics?: boolean;
    loading?: boolean;
    /** Show the supplied startIcon in a square button, keeping children as its accessible label. */
    iconOnly?: boolean;
    tooltipLabel?: string;
    splitButtonArrow?: boolean;
    disabledTooltipLabel?: string;
    dataQa?: string;
    to?: string;
    href?: string;
    target?: string;
    rel?: string;
    download?: string | boolean;
    'aria-label'?: string;
    'aria-expanded'?: boolean;
    'aria-pressed'?: boolean;
    'aria-describedby'?: string;
};

export type FilledButtonProps = {
    kind: 'filled';
    color: ButtonColor;
};

export type NonFilledButtonProps = {
    kind: Exclude<ButtonKind, 'filled'>;
    color: ButtonColor;
};

export type ButtonProps = BaseButtonProps & (FilledButtonProps | NonFilledButtonProps);

function mapVariant(kind: ButtonKind) {
    switch (kind) {
        case 'filled':
            return 'contained';
        default:
            return kind;
    }
}

function mapColor(color: ButtonColor) {
    switch (color) {
        case 'critical':
            return 'danger';
        default:
            return color;
    }
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            children,
            kind,
            color,
            size = 'standard',
            onClick,
            type = 'button',
            fullWidth,
            startIcon,
            endIcon,
            disabled,
            disabledTooltipLabel,
            trackingId,
            trackingProperties,
            disableAnalytics = false,
            loading,
            iconOnly = false,
            tooltipLabel,
            splitButtonArrow = false,
            dataQa,
            to,
            autoFocus,
            sx,
            style,
            className,
            onMouseDown,
            href,
            target,
            rel,
            download,
            'aria-label': ariaLabel,
            'aria-expanded': ariaExpanded,
            'aria-pressed': ariaPressed,
            'aria-describedby': ariaDescribedBy,
        },
        ref,
    ) => {
        const track = useTrack();
        const trackRef = useValueRef(track);
        const trackingPropertiesRef = useValueRef(trackingProperties);
        const disabledRef = useValueRef(disabled);
        useEffect(() => {
            if (disableAnalytics) return;
            trackRef.current(AnalyticsEvent.ElementDisplayed, {
                ...trackingPropertiesRef.current,
                trackingId: trackingId,
                label: splitButtonArrow ? 'Arrow Icon' : children?.toString() || null,
                elementType: DisplayElementType.BUTTON,
                eventType: AnalyticsEventType.ANALYTICS,
                link: null,
                option: null,
                disabled: disabledRef.current,
            });
        }, [
            children,
            disableAnalytics,
            splitButtonArrow,
            disabledRef,
            trackRef,
            trackingId,
            trackingPropertiesRef,
        ]);

        const button = React.createElement(
            StyledButton as any,
            {
                ref,
                type,
                $size: size,
                variant: mapVariant(kind),
                color: mapColor(color),
                fullWidth,
                startIcon: iconOnly ? undefined : loading ? (
                    <CircularProgress size={17} color="inherit" />
                ) : (
                    startIcon
                ),
                endIcon: iconOnly ? undefined : endIcon,
                disabled: disabled || loading,
                autoFocus,
                sx,
                style,
                className,
                $splitButtonArrow: splitButtonArrow,
                $iconOnly: iconOnly,
                disableRipple: true,
                disableElevation: true,
                component: to ? RouterLink : undefined,
                to,
                onClick: (event: React.MouseEvent<HTMLButtonElement>) => {
                    onClick?.(event);
                    if (disableAnalytics) return;
                    track(AnalyticsEvent.ElementClicked, {
                        ...trackingProperties,
                        trackingId,
                        label: splitButtonArrow ? 'Arrow Icon' : children?.toString() || null,
                        elementType: ClickElementType.BUTTON,
                        eventType: AnalyticsEventType.ANALYTICS,
                        option: null,
                        link: to ?? null,
                    });
                },
                onMouseDown,
                href,
                target,
                rel,
                download,
                'aria-label':
                    ariaLabel ?? (iconOnly && typeof children === 'string' ? children : undefined),
                'aria-expanded': ariaExpanded,
                'aria-busy': loading || undefined,
                'aria-pressed': ariaPressed,
                'aria-describedby': ariaDescribedBy,
                'data-button-name': children?.toString(),
                'data-qa': dataQa,
            },
            iconOnly ? (
                loading ? (
                    <CircularProgress size={17} color="inherit" />
                ) : (
                    startIcon
                )
            ) : (
                <Typography role={size === 'small' ? 'button-small' : 'button'} color="inherit">
                    {children}
                </Typography>
            ),
        );

        if (iconOnly || tooltipLabel) {
            return (
                <Tooltip
                    title={(disabled && disabledTooltipLabel) || tooltipLabel || children}
                    arrow
                >
                    <Box component="span" display="inline-flex">
                        {button}
                    </Box>
                </Tooltip>
            );
        }

        if (disabled && disabledTooltipLabel) {
            return (
                <Box display="flex">
                    <Tooltip title={disabledTooltipLabel} arrow>
                        <Box>{button}</Box>
                    </Tooltip>
                </Box>
            );
        }

        return button;
    },
);
